import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Tooltip } from 'antd';
import CustomModal from '../../components/common/CustomModal';
import { FiPlus, FiHelpCircle } from 'react-icons/fi';
import { PiEyeLight } from 'react-icons/pi';
import { LiaEdit, LiaTrashAlt } from 'react-icons/lia';
import { HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineClock } from 'react-icons/hi';
import Table from '../../components/Table';
import SelectDropdown from '../../components/common/SelectDropdown';
import shared from './shared';
import Layout from '../../components/global/layout';
import { statusModel } from '../../models/Status.model';
import { useSelector } from 'react-redux';
import methodModel from '../../methods/methods';
import { FaEdit, FaEye } from 'react-icons/fa';
import { MdDelete, MdDone } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';

function Html({
  sorting,
  filter,
  edit,
  view,
  statusChange,
  pageChange,
  count,
  deleteItem,
  clear,
  filters,
  setFilter,
  total,
  loaging,
  data,
  changestatus,
  sortClass,
  approvalChange,   // (id, status, reason?) => void  — pass this from parent
}) {
  const user = useSelector((state) => state.user);

  // ── Reject reason modal state ──────────────────────────────────────────
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);   // itm being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError]   = useState('');
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);

  const openRejectModal = (itm) => {
    setRejectTarget(itm);
    setRejectReason('');
    setRejectError('');
    setRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      setRejectError('Please enter a reason for rejection.');
      return;
    }
    approvalChange(rejectTarget?.id || rejectTarget?._id, 'rejected', rejectReason.trim());
    setRejectModal(false);
    setRejectTarget(null);
    setRejectReason('');
  };

  // ── Approval action cell ───────────────────────────────────────────────
  const ApprovalCell = ({ itm }) => {
    const status = itm?.approvalStatus;

    if (status === 'approved') {
      return (
        <Tooltip title="Approved">
          <span className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#E5F9E6] text-[#00BF06]">
            <MdDone />
          </span>
        </Tooltip>
      );
    }

    if (status === 'rejected') {
      return (
        <Tooltip title="Rejected">
          <span className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#F8E0DE] text-[#EE5B4F]">
            <IoClose />
          </span>
        </Tooltip>
      );
    }

    // pending (or any other value) — show both approve + reject
    return (
      <>
        <Tooltip title="Approve">
          <span
            onClick={() => approvalChange(itm?.id || itm?._id, 'approved')}
            className="border cursor-pointer hover:bg-green-500 rounded-full bg-[#fff] border-green-200 w-8 h-8 flex items-center justify-center text-xl hover:text-white text-green-500"
          >
            <HiOutlineCheckCircle />
          </span>
        </Tooltip>  
        <Tooltip title="Reject">
          <span
            onClick={() => openRejectModal(itm)}
            className="border cursor-pointer hover:bg-red-500 rounded-full bg-[#fff] border-red-200 w-8 h-8 flex items-center justify-center text-xl hover:text-white text-red-400"
          >
            <HiOutlineXCircle />
          </span>
        </Tooltip>
      </>
    );
  };

  const columns = [
    {
      key: 'name',
      name: 'Name',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row?.logo ? (
            <img
              src={methodModel.noImg(row.logo, 'venue')}
              className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0"
              alt={row.name}
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-[#CC990012] flex items-center justify-center flex-shrink-0">
              <span className="text-[#CC9900] text-xs font-semibold uppercase">
                {row?.name?.charAt(0) || '?'}
              </span>
            </div>
          )}
          <span className="font-medium text-gray-800 capitalize">{row?.name || '—'}</span>
        </div>
      ),
    },
    {
      key: 'email',
      name: 'Email',
      render: (row) => (
        <span className="text-gray-600 text-sm">{row?.email || '—'}</span>
      ),
    },
    {
      key: 'type',
      name: 'Type',
      render: (row) => (
        row?.type ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#CC990012] text-[#CC9900] capitalize">
            {row.type}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        )
      ),
    },

    {
      key: 'status',
      name: 'Status',
      render: (row) => (
        <div
          className=""
          onClick={() =>
            methodModel.hasPermission(user, shared.permissionKey, 'edit')
              ? statusChange(row)
              : null
          }
        >
          <span className={`w-fit min-w-[68px] text-center cursor-pointer text-xs !px-3 py-1 flex items-center justify-center hover:opacity-80 border !rounded-full capitalize ${row.status == 'inactive' ? 'bg-[#ffefef] text-[#ff3b3b] border-[#ffa6a6]' : 'bg-[#E5F9E6] text-[#00BF06] border-[#ADE6AF]'}`}>
              {row.status == 'inactive' ? 'inactive' : 'active'}
          </span>
        </div>
      ),
    },
    {
      key: 'action',
      name: 'Action',
      render: (itm) => (
        <div className="flex items-center justify-start gap-1.5">

          <Tooltip placement="top" title="View">
            <a
              onClick={() => view(itm?.id || itm?._id)}
              className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#E5EFFD] text-[#005AEF]"
            >
              <FaEye />
            </a>
          </Tooltip>

          {methodModel.hasPermission(user, shared.permissionKey, 'edit') && (
            <Tooltip placement="top" title="Edit">
              <a
                onClick={() => edit(itm?.id || itm?._id)}
                className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#F2F2F2] text-[#797979]"
              >
                <FaEdit />
              </a>
            </Tooltip>
          )}

          {methodModel.hasPermission(user, shared.permissionKey, 'delete') && (
            <Tooltip placement="top" title="Delete">
              <span
                onClick={() => deleteItem(itm?.id || itm?._id)}
                className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#F8E0DE] text-[#EE5B4F]"
              >
                <MdDelete />
              </span>
            </Tooltip>
          )}

          {/* ── Approval actions ── */}
          <ApprovalCell itm={itm} />

        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex flex-wrap justify-between items-center gap-y-4 mb-5">
        <div>
          <h3 className="text-2xl font-semibold text-[#111827]">Venue Management</h3>
        </div>
        <div className="flex">
          {methodModel.hasPermission(user, shared.permissionKey, 'edit') && (
            <Link
              className="bg-primary leading-10 h-10 flex justify-center  items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer min-w-[94px]"
              to={`/${shared.url}/add`}
            >
              <FiPlus className="text-lg text-white" />
              Add Venue
            </Link>
          )}
        </div>
      </div>

      <div className="">
        <div className="flex items-center flex-wrap gap-2 mb-4">

          {/* Search */}
          <form
            className="flex items-center max-w-sm gap-2"
            onSubmit={(e) => { e.preventDefault(); filter(); }}
          >
            <label htmlFor="simple-search" className="sr-only">Search</label>
            <div className="relative w-full">
              <input
                type="text"
                id="simple-search"
                value={filters.search}
                onChange={(e) => {
                  setFilter({ ...filters, search: e.target.value });
                  if (e.target.value === '') filter({ search: '' });
                }}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-full focus:ring-orange-500 focus:border-[#063688] block w-full dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-orange-500 dark:focus:border-orange-500 h-10 px-5 pr-11"
                placeholder="Search venues..."
              />
              {filters?.search && (
                <i
                  className="fa fa-times absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm cursor-pointer"
                  aria-hidden="true"
                  onClick={() => clear()}
                />
              )}
                          <button
              type="submit"
              className="text-[#2D2D2D] absolute top-[13px] right-[15px] hover:opacity-80"
            >
              <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
              </svg>
              <span className="sr-only">Search</span>
            </button>
            </div>

          </form>

          {/* Status filter */}
          <div className="flex gap-2 ml-auto">
            <SelectDropdown
              id="statusDropdown"
              displayValue="name"
              placeholder="All Status"
              theme="search"
              isClearable={false}
              intialValue={filters.status}
              result={(e) => { changestatus(e.value); }}
              options={statusModel.StatusList}
            />
            {filters.status && (
              <button
                onClick={() => clear()}
                className="bg-primary leading-10 h-10 inline-block shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-lg"
              >
                Reset
              </button>
            )}
          </div>

        </div>

        {!loaging ? (
          <Table
            className="mb-3"
            data={data}
            columns={columns}
            page={filters.page}
            count={filters.count}
            filters={filters}
            total={total}
            result={(e) => {
              if (e.event === 'page')  pageChange(e.value);
              if (e.event === 'sort')  { sorting(e.value); sortClass(e.value); }
              if (e.event === 'count') count(e.value);
            }}
          />
        ) : (
          <></>
        )}
      </div>

      {/* ── Reject Reason Modal ── */}
      <CustomModal
        open={rejectModal}
        onClose={() => setRejectModal(false)}
        width={500}
      >
        <div className="p-6">
          <div className="flex items-center gap-2 text-red-500 mb-4">
            <HiOutlineXCircle className="text-xl" />
            <span className="font-semibold text-base">Reject Venue</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            You are rejecting{' '}
            <span className="font-semibold text-gray-800">{rejectTarget?.name}</span>.
            Please provide a reason.
          </p>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            className="w-full border border-gray-300 rounded-lg text-sm px-3 py-2 outline-none focus:border-red-400 resize-none"
            placeholder="Enter reason for rejection..."
            value={rejectReason}
            onChange={(e) => { setRejectReason(e.target.value); setRejectError(''); }}
          />
          {rejectError && (
            <p className="text-red-500 text-xs mt-1">{rejectError}</p>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setRejectModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectConfirm}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Confirm Reject
            </button>
          </div>
        </div>
      </CustomModal>

      {/* How It Works Guide Modal */}
      <CustomModal
        open={isHelpModalVisible}
        onClose={() => setIsHelpModalVisible(false)}
        width={650}
      >
        <div className="flex flex-col font-sans">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#CC9900] via-[#E6B800] to-[#CC9900] p-6 text-white relative">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold leading-snug">
                  💡 How Venue Approvals & Management Works
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Understanding venue registrations, reviews, and manual additions
                </p>
              </div>
            </div>
          </div>

          {/* Ticket Dotted Separator */}
          <div className="relative bg-gray-50" style={{ height: '28px' }}>
            <svg className="absolute left-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M0,0 C14,0 14,28 0,28 Z" fill="white" />
            </svg>
            <div className="absolute" style={{ top: '50%', left: '14px', right: '14px', transform: 'translateY(-50%)', borderTop: '1.5px dashed #d1d5db' }} />
            <svg className="absolute right-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M14,0 C0,0 0,28 14,28 Z" fill="white" />
            </svg>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              TavLoy admins manage and review all business venues operating on the platform. Keep in mind the following verification procedures:
            </p>

            <div className="space-y-4">
              {/* Self-Registration Reviews */}
              <div className="flex gap-4 p-4 rounded-xl border border-[#CC9900]/20 bg-[#CC9900]/5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#CC9900]/10 text-[#CC9900] flex-shrink-0">
                  <HiOutlineClock size={18} />
                </span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">Self-Registration & Review</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    When new venues register themselves online, their profiles are added to this dashboard with a pending approval status. Admin action is required to verify and authorize them.
                  </p>
                </div>
              </div>

              {/* Approve & Reject logic */}
              <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-200/50 text-gray-500 flex-shrink-0">
                  <IoClose size={16} />
                </span>
                <div>
                  <h4 className="font-bold text-gray-700 text-sm mb-1">Approve and Reject Actions</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <span className='text-[#008000]'>Approve :</span> Clicking the green checkmark icon instantly activates the venue account.
                  </p>
                   <p className="text-xs text-gray-600 leading-relaxed">
                     <span className='text-[#cc0a0a]'>Reject :</span> Clicking the red cross icon opens a dialog prompting you to enter a mandatory reason for rejection.
                  </p>
                </div>
              </div>

              {/* Manual Add auto acceptance */}
              <div className="flex gap-4 p-4 rounded-xl border border-green-100 bg-green-50/30">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 text-green-600 font-bold text-sm flex-shrink-0">
                  <FiPlus size={16} />
                </span>
                <div>
                  <h4 className="font-bold text-[#00BF06] text-sm mb-1">Manual Creation (Auto-Accepted)</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    If an administrator manually registers a venue by clicking the + Add Venue button at the top-right, the venue's approval status is automatically set to approved/accepted by default.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => setIsHelpModalVisible(false)}
              className="px-5 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all text-xs font-semibold"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Floating Help Button */}
      <Tooltip placement="left" title="How It Works">
        <button
          type="button"
          onClick={() => setIsHelpModalVisible(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-[#CC9900] text-white shadow-lg hover:bg-[#B38000] hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <FiHelpCircle className="text-2xl" />
        </button>
      </Tooltip>
    </Layout>
  );
}

export default Html;